
let _ = require('../_.js');
let $ = jQuery;

// Per-round countdown timers: {auction_round_id: {local_end_ms}}
let _countdown_timers = {};

// One second-aligned ticker redraws every countdown, and each local end time is snapped
// to a whole second, so all clocks on the page change digits together.
// Fires a few ms past each boundary so ceil(remaining / 1000) has already stepped.
const TICK_OFFSET_MS = 5;
let _tick_timeout = null;

// Cached mapping of item_id -> auction_round_id (built on init and item updates)
let _item_round_map = {};

// Cached whole farm round id for enterprise auctions
let _enterprise_wf_round_id = null;

_.AuctionUI_Countdown = class AuctionUI_Countdown {

    static init() {
        // Sealed bid uses auto-close countdown, not this
        if (_.AuctionUI.auction_is_sealed_bid()) return;

        // Build item->round map and listen for updates
        _.AuctionUI.on_update_auction_items(_build_item_round_map);

        // WebSocket listener for countdown updates
        _.on_websocket_receive(function (topic, data) {
            if (topic !== "countdown_update") return;

            let round_id = data.auction_round_id;

            if (data.action === "start") {
                _start_timer(round_id, data.remaining_ms);
            }

            if (data.action === "stop") {
                _stop_timer(round_id);
            }

            // The server's full list follows (sent on every connect)
            if (data.action === "reset") {
                _countdown_timers = {};
            }

            _refresh_displays();
        });

        // While the WS server is unreachable nothing enforces a countdown: show none until it lists them again
        _.on_websocket_disconnected(function () {
            _countdown_timers = {};
            _refresh_displays();
        });

        // Current timer state from the WS server, on every (re)connect
        _.websocket_send_subscribe("countdown_check", {});
    }
}

// Build mapping of item_id -> round_id from current auction items
async function _build_item_round_map() {
    let items = await _.ac_state.auction.auction_items.fetch();
    if (!items) return;

    _enterprise_wf_round_id = null;

    for (let item of items) {
        if (item.auction_round && item.auction_round.id) {
            _item_round_map[item.id] = item.auction_round.id;

            // Cache whole farm round for enterprise
            if (_.AuctionUI.auction_is_enterprise()) {
                let round = await item.auction_round.fetch();
                if (round && round.enterprise_is_whole_farm) {
                    _enterprise_wf_round_id = round.id;
                }
            }
        }
    }

    _refresh_displays();
}

// remaining_ms 0 is a timer held at 0:00; it keeps displaying 0:00 until the server sends "stop"
function _start_timer(round_id, remaining_ms) {
    _stop_timer(round_id);

    _countdown_timers[round_id] = { local_end_ms: remaining_ms > 0 ? Math.round((Date.now() + remaining_ms) / 1000) * 1000 : Date.now() };

    if (!_tick_timeout) _schedule_tick();
}

function _stop_timer(round_id) {
    delete _countdown_timers[round_id];
}

function _schedule_tick() {
    _tick_timeout = setTimeout(function () {
        _refresh_displays();
        if (Object.keys(_countdown_timers).length) {
            _schedule_tick();
        } else {
            _tick_timeout = null;
        }
    }, 1000 - (Date.now() % 1000) + TICK_OFFSET_MS);
}

// Get remaining ms for a round, or null if no active timer
// Returns 0 for timers that reached zero but haven't been cleared by the server
function _get_remaining(round_id) {
    let timer = _countdown_timers[round_id];
    if (!timer) return null;
    let remaining = timer.local_end_ms - Date.now();
    return remaining > 0 ? remaining : 0;
}

// Get the round_id relevant to the current bid area
function _get_bid_area_round_id() {
    if (_.AuctionUI.auction_is_choice()) {
        return _.ac_state.current_round_id || null;
    }
    if (_.AuctionUI.auction_is_enterprise() && _enterprise_wf_round_id) {
        return _enterprise_wf_round_id;
    }
    let item_id = _.ac_state.current_item_id;
    if (item_id && _item_round_map[item_id]) {
        return _item_round_map[item_id];
    }
    return null;
}

function _format_time_full(remaining_ms) {
    let total_seconds = Math.ceil(remaining_ms / 1000);
    let minutes = Math.floor(total_seconds / 60);
    let seconds = total_seconds % 60;
    return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
}

function _format_time_short(remaining_ms) {
    let total_seconds = Math.ceil(remaining_ms / 1000);
    if (total_seconds < 60) {
        return total_seconds + "s";
    }
    let minutes = Math.floor(total_seconds / 60);
    let seconds = total_seconds % 60;
    return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
}

function _get_color(remaining_ms) {
    let total_seconds = Math.ceil(remaining_ms / 1000);
    if (total_seconds <= 30) return "#dc3545";
    if (total_seconds <= 60) return "#fd7e14";
    return "";
}

function _refresh_displays() {
    _refresh_bid_area();
    _refresh_tract_overlays();
}

function _refresh_bid_area() {
    let $el = $(".ac-countdown-bid-area");
    if (!$el.length) return;

    let round_id = _get_bid_area_round_id();
    let remaining = round_id !== null ? _get_remaining(round_id) : null;

    if (remaining !== null) {
        let color = _get_color(remaining);
        let time_html = color
            ? '<span style="color:' + color + ';">' + _format_time_full(remaining) + '</span>'
            : _format_time_full(remaining);
        $el.html("Auction closes in: " + time_html);
        $el.show();
    } else {
        $el.hide();
    }
}

function _refresh_tract_overlays() {
    if (_.AuctionUI.auction_is_sealed_bid()) {
        $(".ac-countdown-thumbnail-overlay").hide();
        return;
    }

    let $items = $(".ac-component-bid-auction-lots-item").not(".TEMPLATE");

    $items.each(function () {
        let $component = $(this);
        let item_id = $component.data("cid");
        let $overlay = $component.find(".ac-countdown-thumbnail-overlay");
        if (!$overlay.length) return;

        let round_id;
        if (_.AuctionUI.auction_is_choice()) {
            // Choice: show countdown on available items only (sold items have no active round)
            if ($component.hasClass('lots-item-color-unavailable')) {
                $overlay.hide();
                return;
            }
            round_id = _.ac_state.current_round_id;
        } else if (_.AuctionUI.auction_is_enterprise() && _enterprise_wf_round_id) {
            round_id = _enterprise_wf_round_id;
        } else {
            round_id = _item_round_map[item_id];
        }

        let remaining = round_id ? _get_remaining(round_id) : null;

        if (remaining !== null) {
            let color = _get_color(remaining);
            let $img = $component.find(".ac-component-bid-auction-lots-item-photo-thumbnail");
            if ($img.length) {
                let img_pos = $img.position();
                $overlay.css({
                    "display": "block",
                    "color": color || "white",
                    "top": img_pos.top + "px",
                    "left": img_pos.left + "px",
                    "width": $img.outerWidth() + "px",
                    "height": $img.outerHeight() + "px",
                    "line-height": $img.outerHeight() + "px",
                });
            }
            $overlay.text(_format_time_short(remaining));
        } else {
            $overlay.hide();
        }
    });
}
